import { baseApi } from './baseApi';
import { Brand } from 'models';
import {
  QueryParams,
  TAG_TYPES,
  CreateBrandRequest,
  UpdateBrandRequest,
  ReplaceRequest,
  ReplaceResponse,
  CheckUsedResponse,
} from 'types';
import { getQueryParams } from 'utils';

const URL_BRAND = 'brands';

const brandApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBrands: builder.query<
      {
        data: {
          rows: Brand[];
          count: number;
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const query = getQueryParams(queryParams);
        if (query === '') return { url: URL_BRAND, method: 'GET' };
        return {
          url: `${URL_BRAND}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result?.data.rows || []).map(({ id }) => ({ type: TAG_TYPES.Brand, id })),
              { type: TAG_TYPES.Brand, id: 'LIST' },
            ]
          : [{ type: TAG_TYPES.Brand, id: 'LIST' }];
      },
    }),

    getBrandById: builder.query<Brand, string>({
      query: (id) => ({
        url: `${URL_BRAND}/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: TAG_TYPES.Brand, id }],
    }),

    createBrand: builder.mutation<Brand, CreateBrandRequest>({
      query: (data) => ({
        url: URL_BRAND,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.Brand, id: 'LIST' }],
    }),

    updateBrand: builder.mutation<Brand, UpdateBrandRequest>({
      query: ({ id, ...patch }) => ({
        url: `${URL_BRAND}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Brand, id },
        { type: TAG_TYPES.Brand, id: 'LIST' },
      ],
    }),

    deleteBrand: builder.mutation<void, string>({
      query: (id) => ({
        url: `${URL_BRAND}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: TAG_TYPES.Brand, id },
        { type: TAG_TYPES.Brand, id: 'LIST' },
      ],
    }),

    checkBrandUsage: builder.query<CheckUsedResponse, string>({
      query: (id) => ({
        url: `${URL_BRAND}/${id}/check-used`,
        method: 'POST',
      }),
      providesTags: (result, error, id) => [{ type: TAG_TYPES.Brand, id: `CHECK_USED_${id}` }],
    }),

    deactivateBrand: builder.mutation<ReplaceResponse, { id: string; payload: ReplaceRequest }>({
      query: ({ id, payload }) => ({
        url: `${URL_BRAND}/${id}/deactivate`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Brand, id },
        { type: TAG_TYPES.Brand, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
        { type: TAG_TYPES.Category, id: 'LIST' },
      ],
    }),

    deleteBrandWithReplacement: builder.mutation<ReplaceResponse, { id: string; payload: ReplaceRequest }>({
      query: ({ id, payload }) => ({
        url: `${URL_BRAND}/${id}/delete`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.Brand, id },
        { type: TAG_TYPES.Brand, id: 'LIST' },
        { type: TAG_TYPES.Product, id: 'LIST' },
        { type: TAG_TYPES.Category, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetBrandsQuery: useGetAllBrandsQuery,
  useLazyGetBrandsQuery: useLazyGetAllBrandsQuery,
  useGetBrandByIdQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useDeleteBrandMutation,
  useLazyCheckBrandUsageQuery,
  useDeactivateBrandMutation,
  useDeleteBrandWithReplacementMutation,
} = brandApiService;
